"use client";

/**
 * Инлайн-блок брони в списке (поиск-режим) — pixel-faithful копия BookingPanel
 * Солнышко: серая обёртка → белая карточка (max 1100) → фото слева 50% + детали
 * справа (название, чипы, сетка ТТХ 5 кол., серый блок телефона + 2 согласия,
 * «Итого» + кнопка). Перекрашено в синий (#0039A6), цены — красным (#d52b1e).
 * Даты из последнего поиска → createBooking → /booking?b={uid}.
 */

import Img from '@/components/Img';
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import PhoneInput from "@/components/PhoneInput";
import { readPhoneCookie, writePhoneCookie } from "@/lib/client-cookie";
import { useToast } from "@/components/ui/Toast";
import { createBooking, type SearchParams } from "@/lib/api";
import { track } from "@/lib/metrika";
import type { Car } from "@/components/CarCard";
import { useLocale } from "@/lib/locale-context";
import { carSpecLabel } from "@/lib/car-spec-i18n";
import type { Locale } from "@/lib/locale-utils";

const PLACEHOLDER = "/images/catalog-car.png";

function daysWord(n: number, locale: Locale): string {
  if (locale === "en") return n === 1 ? "day" : "days";
  const a = n % 10, b = n % 100;
  if (a === 1 && b !== 11) return "день";
  if (a >= 2 && a <= 4 && (b < 10 || b >= 20)) return "дня";
  return "дней";
}

// Подпись периода для строки «ставка × N»: период = total/perDay (учитывает
// дробные сутки 1С, напр. 2.5). Целый → склоняем слово; дробный → «N сут.».
export function periodLabel(total: number, perDay: number, fallbackDays: number, locale: Locale): string {
  const raw = perDay > 0 && total > 0 ? total / perDay : fallbackDays;
  const rounded = Math.round(raw * 10) / 10;
  const whole = Math.round(rounded);
  if (Math.abs(rounded - whole) < 0.05) return `${whole} ${daysWord(whole, locale)}`;
  return `${rounded} ${locale === "en" ? "days" : "сут."}`;
}

function classIcon(cls: string): string {
  const c = (cls || "").toLowerCase();
  if (/люкс|luxury/.test(c)) return "/icons/class/lux.svg";
  if (/бизнес/.test(c)) return "/icons/class/business.svg";
  if (/промо|эконом/.test(c)) return "/icons/class/promo.svg";
  if (/внедорож|suv|джип/.test(c)) return "/icons/class/jeep.svg";
  if (/кроссов/.test(c)) return "/icons/class/suv.svg";
  if (/минив[эе]н|minivan|van/.test(c)) return "/icons/class/van.svg";
  if (/кабриол|convertible/.test(c)) return "/icons/class/cab.svg";
  if (/комфорт/.test(c)) return "/icons/class/comfort.svg";
  return "/icons/class/comfort.svg";
}
const COLOR_HEX: Record<string, string> = {
  "белый": "#f5f5f5", "чёрный": "#1a1a1a", "черный": "#1a1a1a", "серый": "#9aa0a6",
  "серебристый": "#c8ccd0", "серебряный": "#c8ccd0", "красный": "#d52b1e", "синий": "#1e4fd5",
  "голубой": "#5aa9e6", "зелёный": "#2e9c50", "зеленый": "#2e9c50", "жёлтый": "#f5c518",
  "желтый": "#f5c518", "оранжевый": "#f08020", "коричневый": "#7a4f2b", "бежевый": "#d9c6a5",
  "золотой": "#caa84a", "золотистый": "#caa84a", "фиолетовый": "#7a3fd5",
};
const colorToHex = (n: string) => COLOR_HEX[(n || "").trim().toLowerCase()] || "#9aa0a6";

const td: React.CSSProperties = { display: "flex", alignItems: "flex-start", gap: 4, color: "#2B2F33", fontSize: 12, fontWeight: 500, lineHeight: 1.2, minWidth: 0 };
const ic = (src: string) => (
  <Img src={src} alt="" style={{ width: 13, height: 13, objectFit: "contain", flexShrink: 0 }} />
);
// Класс-иконки (силуэты машин ~2.7:1) — шире, чтобы читались наравне с прочими.
const classIc = (src: string) => (
  <Img src={src} alt="" style={{ width: 20, height: 13, objectFit: "contain", flexShrink: 0 }} />
);

export default function InlineBookingPanel({
  car, station, citySlug, dates, onClose, flat = false,
}: {
  car: Car;
  station: string;
  citySlug: string;
  dates: (SearchParams & { days: number }) | null;
  onClose?: () => void;
  flat?: boolean;
}) {
  const router = useRouter();
  const { t, locale } = useLocale();
  const toast = useToast();
  const [phone, setPhone] = useState("");
  const [dial, setDial] = useState("+7");

  // Запоминание телефона (как «last_phone» на Солнышке). PhoneInput стартует с
  // РФ (+7), поэтому префилл делаем только для +7 — иначе маска показала бы
  // чужую страну; для остальных стран номер вводится заново. Пишем — всегда.
  useEffect(() => {
    const cookie = readPhoneCookie();
    if (!cookie) return;
    const sp = cookie.indexOf(" ");
    const d = sp > 0 ? cookie.slice(0, sp) : "+7";
    const rest = sp > 0 ? cookie.slice(sp + 1) : cookie;
    if (d === "+7" && rest) { setPhone(rest); setDial("+7"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (phone) writePhoneCookie(`${dial} ${phone}`.trim());
  }, [phone, dial]);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreePersonal, setAgreePersonal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const cur = car.currencySym || "₽";
  const days = dates?.days || 1;
  const perDay = Math.round((car.priceKopecks ?? 0) / 100);
  // Итого за период из 1С (учитывает дробные сутки). Фолбэк — сутки×дни.
  const costRub = car.costKopecks && car.costKopecks > 0 ? Math.round(car.costKopecks / 100) : 0;
  const total = costRub || perDay * days;
  const oldPerDay = car.price.old ? Number(car.price.old.replace(/\D/g, "")) || 0 : 0;
  // Перечёркнутая сумма — пропорцией от cost, чтобы совпасть по сут.
  const oldTotal = oldPerDay > 0
    ? (costRub > 0 && perDay > 0 ? Math.round((total * oldPerDay) / perDay) : oldPerDay * days)
    : 0;
  const fmt = (n: number) => n.toLocaleString("ru-RU");

  // Сетка ТТХ (как у Солнышко): год/КПП/топливо/двигатель/места/багаж/привод/класс/цвет.
  const tiles: { icon: React.ReactNode; value: React.ReactNode }[] = [];
  if (car.year && car.year > 0) tiles.push({ icon: ic("/icons/spec-year.png"), value: car.year });
  if (car.features.transmission && car.features.transmission !== "—") tiles.push({ icon: ic("/icons/transmission.svg"), value: carSpecLabel(car.features.transmission, locale) });
  if (car.features.fuel && car.features.fuel !== "—") tiles.push({ icon: ic("/icons/fuel.svg"), value: carSpecLabel(car.features.fuel, locale) });
  if (car.features.drive && car.features.drive !== "—") tiles.push({ icon: ic("/icons/spec-engine.png"), value: carSpecLabel(car.features.drive, locale) });
  if (car.seatsNum && car.seatsNum > 0) tiles.push({ icon: ic("/icons/seat.svg"), value: carSpecLabel(`${car.seatsNum} мест`, locale) });
  if (car.bagsNum && car.bagsNum > 0) tiles.push({ icon: ic("/icons/baggage.svg"), value: car.bagsNum });
  if (car.driveUnit) tiles.push({ icon: ic("/icons/drive.svg"), value: carSpecLabel(car.driveUnit, locale) });
  if (car.carClass) tiles.push({ icon: classIc(classIcon(car.carClass)), value: carSpecLabel(car.carClass, locale) });
  if (car.color) tiles.push({ icon: <span style={{ display: "inline-block", width: 12, height: 12, borderRadius: "50%", background: colorToHex(car.color), border: "1px solid #D0D0D5" }} />, value: carSpecLabel(car.color, locale) });

  async function submit() {
    setError("");
    if (!dates) { toast.show(t("booking.pickDatesFirst"), "error"); return; }
    const fullPhone = `${dial} ${phone}`.trim();
    if (fullPhone.replace(/\D/g, "").length < 11) { toast.show(t("booking.enterFullPhone"), "error"); return; }
    if (!agreePrivacy || !agreePersonal) { toast.show(t("consent.confirmRequired"), "error"); return; }
    setBusy(true);
    try {
      const uid = await createBooking({
        phone: fullPhone, carId: car.id, carName: car.title,
        dateFrom: dates.startDate, dateTo: dates.endDate,
        startTime: dates.startTime, endTime: dates.endTime,
        station, citySlug,
      });
      track("booking_start", { city: citySlug || station });
      router.push(`/booking?b=${encodeURIComponent(uid)}`);
    } catch {
      setError(t("booking.submitFailed"));
      setBusy(false);
    }
  }

  return (
    <div style={{ alignSelf: "stretch", display: "flex", flexDirection: "column", alignItems: "center", background: flat ? "transparent" : "#EDEDED", paddingTop: flat ? 0 : 40, paddingBottom: flat ? 0 : 40, borderRadius: flat ? 0 : 20 }}>
      <div style={{ display: "flex", alignItems: "stretch", background: "#FFFFFF", borderRadius: 30, maxWidth: 1100, width: "100%", position: "relative" }} className="ibp-inner">
        {/* Фото слева 50% */}
        <div className="ibp-photo" style={{ flex: "0 0 50%", width: "50%", display: "flex", alignItems: "center", justifyContent: "center", paddingLeft: 24, boxSizing: "border-box" }}>
          <div style={{ width: "100%", aspectRatio: "3 / 2", overflow: "hidden", position: "relative", borderRadius: 16 }}>
            <Img src={car.image || PLACEHOLDER} alt={car.title} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} onError={(e) => { (e.target as HTMLImageElement).src = PLACEHOLDER; }} />
            {car.isHybrid ? (
              <span style={{ position: "absolute", left: 10, bottom: 10, zIndex: 3, background: "#6D28D9", color: "#fff", fontSize: 11, fontWeight: 600, lineHeight: 1.2, padding: "4px 8px", borderRadius: 7, whiteSpace: "nowrap", boxShadow: "0 2px 8px rgba(109,40,217,0.35)" }}>
                {locale === "en" ? "⚡ PHEV | 🔋 Charging | ⛽ Gasoline" : "⚡ PHEV | 🔋 Зарядка | ⛽ Бензин"}
              </span>
            ) : null}
          </div>
        </div>

        {/* Детали справа */}
        <div className="ibp-details" style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", padding: "24px 36px", minWidth: 0 }}>
          {onClose && (
            <button onClick={onClose} style={{ alignSelf: "flex-end", background: "none", border: "none", cursor: "pointer", padding: 4, marginTop: -8, marginRight: -8, marginBottom: 4 }} aria-label={t("common.close")}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          )}

          <span style={{ color: "#000", fontSize: 26, fontWeight: "bold", marginBottom: 6 }}>{car.title}</span>

          {(car.carFeatures || []).length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {(car.carFeatures || []).map((f, i) => (
                <span key={i} style={{ padding: "3px 10px", background: "#dfe8f6", color: "#0039A6", fontSize: 12, fontWeight: 500, borderRadius: 20, lineHeight: 1.4, whiteSpace: "nowrap" }}>{f.name}</span>
              ))}
            </div>
          )}

          {tiles.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", columnGap: 6, rowGap: 8, marginBottom: 18, width: "100%" }}>
              {tiles.map((t, i) => (
                <span key={i} style={td}>
                  <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1 }}>{t.icon}</span>
                  <span style={{ whiteSpace: "normal", overflowWrap: "break-word", minWidth: 0 }}>{t.value}</span>
                </span>
              ))}
            </div>
          )}

          {/* Серый блок: телефон + согласия */}
          <div style={{ display: "flex", flexDirection: "column", background: "#EDEDED", borderRadius: 10, padding: "16px 20px", marginBottom: 16, width: "100%", boxSizing: "border-box", gap: 8 }}>
            <PhoneInput value={phone} onChange={(p, d) => { setPhone(p); setDial(d); }} />
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 12, color: "#2B2F33", lineHeight: 1.4, cursor: "pointer" }}>
              <input type="checkbox" checked={agreePrivacy} onChange={(e) => setAgreePrivacy(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, accentColor: "#0039A6", flexShrink: 0 }} />
              <span>{t("consent.privacyPrefix")} <Link href="/legal/privacy" target="_blank" style={{ color: "#0039A6", textDecoration: "underline" }} onClick={(e) => e.stopPropagation()}>{t("consent.privacySite")}</Link></span>
            </label>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 12, color: "#2B2F33", lineHeight: 1.4, cursor: "pointer" }}>
              <input type="checkbox" checked={agreePersonal} onChange={(e) => setAgreePersonal(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, accentColor: "#0039A6", flexShrink: 0 }} />
              <span>{t("consent.personalPrefix2")} <Link href="/legal/personal-data" target="_blank" style={{ color: "#0039A6", textDecoration: "underline" }} onClick={(e) => e.stopPropagation()}>{t("consent.personalData")}</Link></span>
            </label>
          </div>

          {/* Итого + кнопка */}
          <div style={{ display: "flex", alignItems: "center", gap: 20, width: "100%" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ color: "#000", fontSize: 13 }}>{t("booking.total")}</span>
              {oldTotal > 0 && <span style={{ color: "#9E9E9E", fontSize: 13, textDecoration: "line-through", lineHeight: 1 }}>{fmt(oldTotal)} {cur}</span>}
              <span style={{ color: "#d52b1e", fontSize: 20, fontWeight: "bold" }}>{fmt(total)} {cur}</span>
              <span style={{ color: "#888", fontSize: 11 }}>{fmt(perDay)} {cur}{t("booking.perDay")} × {periodLabel(total, perDay, days, locale)}</span>
            </div>
            <button onClick={submit} disabled={busy} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "#0039A6", borderRadius: 10, border: "none", padding: "14px 0", cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1 }}>
              <span style={{ color: "#fff", fontSize: 16, fontWeight: "bold" }}>{busy ? t("booking.loading") : t("btn.continue")}</span>
            </button>
          </div>

          {error && <div style={{ marginTop: 10, color: "#d52b1e", fontSize: 13 }}>{error}</div>}
        </div>
      </div>

      {/* На мобильном фото сверху, детали снизу */}
      <style>{`@media (max-width: 720px){ .ibp-inner{ flex-direction:column } .ibp-photo{ width:100%!important; flex:none!important; padding-left:0!important } .ibp-details{ padding:20px!important } }`}</style>
    </div>
  );
}
